import React, { useMemo } from 'react';
import {
  ReviewDivider,
  ReviewDoubleRow,
  ReviewItem,
  ReviewItemLabel,
  ReviewItemValue,
  ReviewItemValueMuted,
  ReviewSection,
  ReviewSectionTitle
} from '../../../../../components/WizardV2/wizardStylesV2';
import {
  EntityCoverBadge,
  EntityImagesGrid,
  EntityImageThumb,
  EntityImageTile
} from '../../../../../components/EntityImagesGallery';
import {
  getRateTypeLabel,
  formatAmenityFeeDisplay,
  formatOptionalMoneyOrNone
} from './amenityPaymentConfigV2';
import { formatAutoBlockDurationLabel } from './addAmenityScheduleHelpersV2';

function getSelectedDaysLabel(selectedDays, t) {
  if (selectedDays == null) return '—';
  if (Array.isArray(selectedDays)) {
    if (selectedDays.length === 0) return '—';
    return selectedDays.map((d) => d.label).join(', ');
  }
  return selectedDays.label || '—';
}

function AmenityReviewDisplayV2({
  t,
  values,
  amenityTypes,
  amenityImages = [],
  waiverFile = null,
  buildingsSummary = '—'
}) {
  const {
    code,
    type_id: typeId,
    selectedDays,
    allow_booking: allowBooking,
    allow_multi_day: allowMultiDayBookings,
    booking_limits_unit: bookingLimitsUnit,
    min_duration_value: minDurationValue,
    max_duration_value: maxDurationValue,
    max_advance_booking: maxAdvanceBooking,
    max_reservation_hours: maxReservationHours,
    min_reservation_hours: minReservationHours,
    allow_same_day_multiple_bookings: allowSameDayMultiple,
    slot_duration_minutes: slotDurationMinutes,
    building_ids: buildingIds,
    require_reservation_form: requireReservationForm,
    require_payment: requirePayment,
    cost_type: costType,
    payment_rate: paymentRate,
    service_charge: serviceCharge,
    deposit,
    require_manager_approval: requireManagerApproval,
    enable_post_booking_auto_block: blockingEnabled,
    post_booking_auto_block_duration: postBlockDuration,
    post_block_description: postBlockDescription
  } = values || {};

  const typeLabel = useMemo(() => {
    if (typeId == null || typeId === '') return '—';
    const idNum = Number(typeId);
    const row = amenityTypes?.find((a) => a.ID === idNum || String(a.ID) === String(typeId));
    return row?.Description || '—';
  }, [amenityTypes, typeId]);

  const buildingLabel =
    Array.isArray(buildingIds) && buildingIds.length > 0 ? t('Buildings') : t('Building');

  const maxDurationLabel = useMemo(() => {
    if (!allowBooking) {
      return t('add_amenity_review_na_booking_off', { defaultValue: '—' });
    }
    if (allowMultiDayBookings) {
      const maxV =
        maxDurationValue != null && maxDurationValue !== '' ? Number(maxDurationValue) : null;
      if (maxV == null || Number.isNaN(maxV)) return '—';
      const unitDays = Number(bookingLimitsUnit) === 1;
      return unitDays
        ? t('add_amenity_review_max_days', { defaultValue: '{{n}} days', n: maxV })
        : t('add_amenity_review_max_hrs', { defaultValue: '{{n}} hrs', n: maxV });
    }
    if (maxReservationHours == null || maxReservationHours === '') return '—';
    return t('add_amenity_review_max_hrs', {
      defaultValue: '{{n}} hrs',
      n: maxReservationHours
    });
  }, [
    allowBooking,
    allowMultiDayBookings,
    bookingLimitsUnit,
    maxDurationValue,
    maxReservationHours,
    t
  ]);

  const availabilityLabel = getSelectedDaysLabel(selectedDays, t);

  const slotLabel = useMemo(() => {
    const m = slotDurationMinutes != null ? Number(slotDurationMinutes) : 30;
    if (m === 60) return t('add_amenity_slot_1_hr', { defaultValue: '1 hr' });
    return t('add_amenity_slot_30_min', { defaultValue: '30 min' });
  }, [slotDurationMinutes, t]);

  const bookingLimitsLabel = useMemo(() => {
    if (!allowBooking) {
      return t('add_amenity_review_na_booking_off', { defaultValue: '—' });
    }
    if (allowMultiDayBookings) {
      const minV =
        minDurationValue != null && minDurationValue !== ''
          ? Number(minDurationValue)
          : 0;
      const maxV =
        maxDurationValue != null && maxDurationValue !== '' ? Number(maxDurationValue) : null;
      if (Number.isNaN(minV) || maxV == null || Number.isNaN(maxV)) {
        return '—';
      }
      const unitDays = Number(bookingLimitsUnit) === 1;
      const unitLabel = unitDays
        ? t('add_amenity_review_limits_days', { defaultValue: 'days' })
        : t('add_amenity_review_limits_hours', { defaultValue: 'hrs' });
      const advance =
        maxAdvanceBooking != null && maxAdvanceBooking !== ''
          ? Number(maxAdvanceBooking)
          : null;
      const advancePart =
        advance != null && !Number.isNaN(advance)
          ? t('add_amenity_review_advance_days', {
              defaultValue: '; up to {{n}} days ahead',
              n: advance
            })
          : '';
      return t('add_amenity_review_booking_limits_multi', {
        defaultValue: '{{min}} – {{max}} {{unit}}{{advance}}',
        min: minV,
        max: maxV,
        unit: unitLabel,
        advance: advancePart
      });
    }
    const minH = minReservationHours != null && minReservationHours !== '' ? Number(minReservationHours) : 0;
    const maxH =
      maxReservationHours != null && maxReservationHours !== ''
        ? Number(maxReservationHours)
        : null;
    if (maxH == null || Number.isNaN(maxH)) return '—';
    const advance =
      maxAdvanceBooking != null && maxAdvanceBooking !== ''
        ? Number(maxAdvanceBooking)
        : null;
    const advancePart =
      advance != null && !Number.isNaN(advance)
        ? t('add_amenity_review_advance_days', {
            defaultValue: '; up to {{n}} days ahead',
            n: advance
          })
        : '';
    return t('add_amenity_review_booking_limits_single', {
      defaultValue: '{{min}} – {{max}} hrs{{advance}}',
      min: minH,
      max: maxH,
      advance: advancePart
    });
  }, [
    allowBooking,
    allowMultiDayBookings,
    maxAdvanceBooking,
    bookingLimitsUnit,
    maxDurationValue,
    minDurationValue,
    minReservationHours,
    maxReservationHours,
    t
  ]);

  const showPaymentSection = allowBooking && !!requirePayment;
  const rateTypeLabel = useMemo(
    () => (showPaymentSection ? getRateTypeLabel(costType, t) : '—'),
    [showPaymentSection, costType, t]
  );
  const bookingFeeDisplay = useMemo(() => {
    if (!showPaymentSection) return '—';
    return formatAmenityFeeDisplay(
      { require_payment: true, cost: paymentRate, cost_type: costType },
      t
    );
  }, [showPaymentSection, paymentRate, costType, t]);
  const serviceChargeDisplay = useMemo(
    () => formatOptionalMoneyOrNone(serviceCharge, t),
    [serviceCharge, t]
  );
  const depositDisplay = useMemo(
    () => formatOptionalMoneyOrNone(deposit, t),
    [deposit, t]
  );

  const postBlockLabel = useMemo(() => {
    if (!blockingEnabled) return null;
    return formatAutoBlockDurationLabel(postBlockDuration ?? slotDurationMinutes, t);
  }, [blockingEnabled, postBlockDuration, slotDurationMinutes, t]);

  const postBlockReason = useMemo(() => {
    if (postBlockDescription == null) return '';
    return String(postBlockDescription).trim();
  }, [postBlockDescription]);

  const enabledText = t('Enabled', { defaultValue: 'Enabled' });
  const disabledText = t('Disabled', { defaultValue: 'Disabled' });

  const imageCount = amenityImages?.length ?? 0;
  const imagesDetailLabel =
    imageCount > 0
      ? t('add_amenity_review_images_count', {
          defaultValue: '{{count}} images',
          count: imageCount
        })
      : t('add_amenity_review_images_none', { defaultValue: 'No Images' });

  return (
    <>
      <ReviewSection>
        <ReviewSectionTitle>
          {t('add_amenity_review_section_details', { defaultValue: 'Amenity Details' })}
        </ReviewSectionTitle>
        <ReviewDoubleRow>
          <ReviewItem>
            <ReviewItemLabel>{t('Name')}</ReviewItemLabel>
            <ReviewItemValue>{code?.trim() ? code : '—'}</ReviewItemValue>
          </ReviewItem>
          <ReviewItem>
            <ReviewItemLabel>
              {t('add_amenity_review_icon_selector', { defaultValue: 'Icon Selector' })}
            </ReviewItemLabel>
            <ReviewItemValue>{typeLabel}</ReviewItemValue>
          </ReviewItem>
        </ReviewDoubleRow>
        {allowBooking ? (
          <>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>{buildingLabel}</ReviewItemLabel>
                <ReviewItemValue>{buildingsSummary}</ReviewItemValue>
              </ReviewItem>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_max_reservation_time', {
                    defaultValue: 'Max Reservation Time'
                  })}
                </ReviewItemLabel>
                <ReviewItemValue>{maxDurationLabel}</ReviewItemValue>
              </ReviewItem>
            </ReviewDoubleRow>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_available_days', { defaultValue: 'Available Days' })}
                </ReviewItemLabel>
                <ReviewItemValue>{availabilityLabel}</ReviewItemValue>
              </ReviewItem>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_images_label', { defaultValue: 'Images' })}
                </ReviewItemLabel>
                <ReviewItemValue>{imagesDetailLabel}</ReviewItemValue>
              </ReviewItem>
            </ReviewDoubleRow>
          </>
        ) : (
          <ReviewDoubleRow>
            <ReviewItem>
              <ReviewItemLabel>{buildingLabel}</ReviewItemLabel>
              <ReviewItemValue>{buildingsSummary}</ReviewItemValue>
            </ReviewItem>
            <ReviewItem>
              <ReviewItemLabel>
                {t('add_amenity_review_available_days', { defaultValue: 'Available Days' })}
              </ReviewItemLabel>
              <ReviewItemValue>{availabilityLabel}</ReviewItemValue>
            </ReviewItem>
          </ReviewDoubleRow>
        )}
      </ReviewSection>

      {allowBooking ? (
        <>
          <ReviewDivider />

          <ReviewSection>
            <ReviewSectionTitle>
              {t('add_amenity_review_section_booking', { defaultValue: 'Booking Rules' })}
            </ReviewSectionTitle>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_require_manager_approval', {
                    defaultValue: 'Require Manager Approval'
                  })}
                </ReviewItemLabel>
                {requireManagerApproval ? (
                  <ReviewItemValue>{enabledText}</ReviewItemValue>
                ) : (
                  <ReviewItemValueMuted>{disabledText}</ReviewItemValueMuted>
                )}
              </ReviewItem>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_allow_multi_day', {
                    defaultValue: 'Allow Multi-Day Bookings'
                  })}
                </ReviewItemLabel>
                {allowMultiDayBookings ? (
                  <ReviewItemValue>{enabledText}</ReviewItemValue>
                ) : (
                  <ReviewItemValueMuted>{disabledText}</ReviewItemValueMuted>
                )}
              </ReviewItem>
            </ReviewDoubleRow>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_allow_same_day_multiple', {
                    defaultValue: 'Allow Multiple Same-Day Bookings'
                  })}
                </ReviewItemLabel>
                {allowSameDayMultiple ? (
                  <ReviewItemValue>{enabledText}</ReviewItemValue>
                ) : (
                  <ReviewItemValueMuted>{disabledText}</ReviewItemValueMuted>
                )}
              </ReviewItem>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_slot_interval', { defaultValue: 'Slot Interval' })}
                </ReviewItemLabel>
                <ReviewItemValue>{slotLabel}</ReviewItemValue>
              </ReviewItem>
            </ReviewDoubleRow>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_booking_limits', { defaultValue: 'Booking Limits' })}
                </ReviewItemLabel>
                <ReviewItemValue>{bookingLimitsLabel}</ReviewItemValue>
              </ReviewItem>
            </ReviewDoubleRow>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_review_post_blocking', {
                    defaultValue: 'Post-Reservation Blocking'
                  })}
                </ReviewItemLabel>
                {blockingEnabled ? (
                  <ReviewItemValue>
                    {`${enabledText} — ${postBlockLabel}`}
                  </ReviewItemValue>
                ) : (
                  <ReviewItemValueMuted>{disabledText}</ReviewItemValueMuted>
                )}
              </ReviewItem>
            </ReviewDoubleRow>
            {blockingEnabled && postBlockReason ? (
              <ReviewDoubleRow>
                <ReviewItem>
                  <ReviewItemLabel>{t('Reason')}</ReviewItemLabel>
                  <ReviewItemValue>{postBlockReason}</ReviewItemValue>
                </ReviewItem>
              </ReviewDoubleRow>
            ) : null}
          </ReviewSection>

          <ReviewDivider />

          <ReviewSection>
            <ReviewSectionTitle>
              {t('add_amenity_review_section_forms', { defaultValue: 'Forms' })}
            </ReviewSectionTitle>
            <ReviewDoubleRow>
              <ReviewItem>
                <ReviewItemLabel>
                  {t('add_amenity_require_amenity_form', { defaultValue: 'Require Amenity Form' })}
                </ReviewItemLabel>
                {requireReservationForm ? (
                  <ReviewItemValue>{enabledText}</ReviewItemValue>
                ) : (
                  <ReviewItemValueMuted>{disabledText}</ReviewItemValueMuted>
                )}
              </ReviewItem>
            </ReviewDoubleRow>
            {requireReservationForm ? (
              <ReviewDoubleRow>
                <ReviewItem>
                  <ReviewItemLabel>
                    {t('add_amenity_review_reservation_form_file', {
                      defaultValue: 'Reservation Form'
                    })}
                  </ReviewItemLabel>
                  <ReviewItemValue>
                    {waiverFile?.name?.trim() ? waiverFile.name : '—'}
                  </ReviewItemValue>
                </ReviewItem>
              </ReviewDoubleRow>
            ) : null}
          </ReviewSection>

          {showPaymentSection ? (
            <>
              <ReviewDivider />
              <ReviewSection>
                <ReviewSectionTitle>{t('Payments', { defaultValue: 'Payments' })}</ReviewSectionTitle>
                <ReviewDoubleRow>
                  <ReviewItem>
                    <ReviewItemLabel>
                      {t('add_amenity_review_rate_type_label', { defaultValue: 'Rate Type' })}
                    </ReviewItemLabel>
                    <ReviewItemValue>{rateTypeLabel}</ReviewItemValue>
                  </ReviewItem>
                  <ReviewItem>
                    <ReviewItemLabel>
                      {t('add_amenity_booking_fee', { defaultValue: 'Booking Fee' })}
                    </ReviewItemLabel>
                    <ReviewItemValue>{bookingFeeDisplay}</ReviewItemValue>
                  </ReviewItem>
                </ReviewDoubleRow>
                <ReviewDoubleRow>
                  <ReviewItem>
                    <ReviewItemLabel>
                      {t('add_amenity_review_service_fee_label', {
                        defaultValue: 'Service Fee'
                      })}
                    </ReviewItemLabel>
                    <ReviewItemValue>{serviceChargeDisplay}</ReviewItemValue>
                  </ReviewItem>
                  <ReviewItem>
                    <ReviewItemLabel>
                      {t('add_amenity_review_deposit_label', { defaultValue: 'Deposit' })}
                    </ReviewItemLabel>
                    <ReviewItemValue>{depositDisplay}</ReviewItemValue>
                  </ReviewItem>
                </ReviewDoubleRow>
              </ReviewSection>
            </>
          ) : null}

          <ReviewDivider />
        </>
      ) : (
        <ReviewDivider />
      )}
      <ReviewSection>
        <ReviewSectionTitle>
          {t('add_amenity_review_section_images', { defaultValue: 'Amenity Images' })}
        </ReviewSectionTitle>
        {imageCount > 0 ? (
          <EntityImagesGrid>
            {amenityImages.map((img, index) => (
              <EntityImageTile key={img.id}>
                {index === 0 ? <EntityCoverBadge>{t('Cover')}</EntityCoverBadge> : null}
                <EntityImageThumb src={img.preview} alt="" draggable={false} />
              </EntityImageTile>
            ))}
          </EntityImagesGrid>
        ) : (
          <ReviewItemValue>
            {t('add_amenity_review_images_none', { defaultValue: 'No Images' })}
          </ReviewItemValue>
        )}
      </ReviewSection>
    </>
  );
}

export default AmenityReviewDisplayV2;
